// 비로그인 카트 store (localStorage 영속).
// 사용자가 로그인하지 않은 상태에서 담은 항목을 보관한다. 로그인 시점에 본 store 의 내용을
// 서버로 머지(POST /cart/merge) 한 뒤 clear() 한다.
//
// 저장 형태: { items: [{ productId, quantity, addedAt }] }
//  - 가격/이름/이미지 스냅샷은 저장하지 않는다. 카트 화면 진입 시 항상 서버에서 현재 상품 정보를
//    조회해 표시하므로 (CartPage 가 사용) 가격 변동/품절/삭제가 즉시 반영된다.
//  - addedAt 은 최근 항목 정렬용 ISO 시각.
//
// 한도 정책:
//  - 같은 상품을 다시 담으면 수량을 합산하고, 99 를 넘으면 99 로 잘라낸다.
//  - 서로 다른 상품 수 50 종이 넘으면 추가를 거절한다 (UI 에서 안내).

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { CART_DISTINCT_ITEM_MAX, CART_ITEM_QUANTITY_MAX } from "@oo/shared";

// 비로그인 상태에서 고른 추가 옵션. 옵션 마스터 값(문구/중량/금액)은 저장하지 않고 id + 개수만 둔다
// (상품 정보와 마찬가지로 표시 시점에 서버의 현재 옵션 값을 조회해 쓴다).
export interface GuestCartItemOption {
  optionId: string;
  quantity: number;
}

export interface GuestCartItem {
  productId: string;
  quantity: number;
  options?: GuestCartItemOption[];
  addedAt: string;
}

interface GuestCartStore {
  items: GuestCartItem[];
  // 동일 productId 면 수량 합산(옵션 수량도 각각 합산). 한도 초과 시 잘라내고 true (capped) 반환.
  addItem: (
    productId: string,
    addQuantity: number,
    options?: GuestCartItemOption[],
  ) => { ok: true; capped: boolean } | { ok: false; reason: "distinct-limit" };
  // 수량 명시값 갱신. 0 이하는 무시 (삭제는 removeItem 사용).
  setQuantity: (productId: string, quantity: number) => void;
  removeItem: (productId: string) => void;
  clear: () => void;
  // 총 항목 수(서로 다른 상품 수).
  count: () => number;
}

// 기존 옵션 선택에 새로 고른 옵션을 합산한다(같은 옵션이면 개수 합, 한도 99).
function mergeGuestOptions(
  existing: GuestCartItemOption[] | undefined,
  added: GuestCartItemOption[] | undefined,
): GuestCartItemOption[] | undefined {
  if ((!existing || existing.length === 0) && (!added || added.length === 0)) {
    return undefined;
  }
  const map = new Map<string, number>();
  for (const o of existing ?? []) {
    map.set(o.optionId, o.quantity);
  }
  for (const o of added ?? []) {
    if (o.quantity <= 0) {
      continue;
    }
    map.set(o.optionId, Math.min(CART_ITEM_QUANTITY_MAX, (map.get(o.optionId) ?? 0) + o.quantity));
  }
  const merged = [...map.entries()].map(([optionId, quantity]) => ({ optionId, quantity }));
  return merged.length > 0 ? merged : undefined;
}

export const useGuestCartStore = create<GuestCartStore>()(
  persist(
    (set, get) => ({
      items: [],
      addItem: (productId, addQuantity, options) => {
        if (addQuantity <= 0) {
          return { ok: true, capped: false } as const;
        }
        const items = get().items.slice();
        const idx = items.findIndex((it) => it.productId === productId);
        if (idx >= 0) {
          const existing = items[idx];
          if (!existing) {
            // 타입 가드. 실제로는 도달 불가.
            return { ok: true, capped: false } as const;
          }
          const target = existing.quantity + addQuantity;
          const next = Math.min(CART_ITEM_QUANTITY_MAX, target);
          const capped = target > CART_ITEM_QUANTITY_MAX;
          items[idx] = {
            ...existing,
            quantity: next,
            options: mergeGuestOptions(existing.options, options),
            addedAt: new Date().toISOString(),
          };
          set({ items });
          return { ok: true, capped } as const;
        }
        if (items.length >= CART_DISTINCT_ITEM_MAX) {
          return { ok: false, reason: "distinct-limit" } as const;
        }
        const next = Math.min(CART_ITEM_QUANTITY_MAX, addQuantity);
        const capped = addQuantity > CART_ITEM_QUANTITY_MAX;
        items.unshift({
          productId,
          quantity: next,
          options: mergeGuestOptions(undefined, options),
          addedAt: new Date().toISOString(),
        });
        set({ items });
        return { ok: true, capped } as const;
      },
      setQuantity: (productId, quantity) => {
        if (quantity <= 0) {
          return;
        }
        const next = Math.min(CART_ITEM_QUANTITY_MAX, quantity);
        set((state) => ({
          items: state.items.map((it) =>
            it.productId === productId
              ? { ...it, quantity: next, addedAt: new Date().toISOString() }
              : it,
          ),
        }));
      },
      removeItem: (productId) =>
        set((state) => ({ items: state.items.filter((it) => it.productId !== productId) })),
      clear: () => set({ items: [] }),
      count: () => get().items.length,
    }),
    {
      name: "oo-user-guest-cart",
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);
